import { mapDTO } from '@twistezo/ts-dto-mapper'
import hash from 'object-hash'

import type { FinanceReportRow, FinanceReportSortBy, FinanceReportSubRow } from '../types'
import type { ColumnSchemaDTO, PeriodDTO, SchemaDTO } from './integrity'
import type { FinanceReportSortByDTO } from './types'
import type { SortInput } from 'components/Table'

type From = SchemaDTO['data']
type To = FinanceReportRow[]

export const fromDTO = (from: From): To =>
  mapDTO<From, To>({
    from,
  }).transform((from: From): To => {
    return Object.entries(from).map(
      ([period, rows]: [PeriodDTO, ColumnSchemaDTO[]]): FinanceReportRow => {
        const total: ColumnSchemaDTO | undefined = rows.find(
          (row: ColumnSchemaDTO): boolean => row.Product === 'TOTAL',
        )

        if (total === undefined) {
          throw Error(`No total row found for period ${period}.`)
        }

        const subRows: FinanceReportSubRow[] = rows
          .filter((subRow: ColumnSchemaDTO): boolean => subRow.Product !== 'TOTAL')
          .map(
            (subRow: ColumnSchemaDTO): Pick<FinanceReportSubRow, 'amount' | 'product'> => ({
              amount: `${subRow.Amount_to_invoice} ${subRow.Currency}`,
              product: subRow.Product,
            }),
          )
          .map((subRow: Pick<FinanceReportSubRow, 'amount' | 'product'>) => ({
            ...subRow,
            id: hash(['sub-row', period, subRow]),
          }))
          .sort((a: FinanceReportSubRow, b: FinanceReportSubRow): number =>
            a.product.localeCompare(b.product),
          )

        const row: Pick<FinanceReportRow, 'amount' | 'period' | 'subRows'> = {
          amount: `${total.Amount_to_invoice} ${total.Currency}`,
          period,
          subRows,
        }

        return {
          ...row,
          id: hash(['row', row]),
        }
      },
    )
  })

export const sortToDTO = (
  sort: SortInput<FinanceReportSortBy>,
): SortInput<FinanceReportSortByDTO> => {
  const { by } = sort

  return { ...sort, by }
}
